import React, { useEffect, useState } from 'react'
import QRCode from 'react-qr-code'
import OtpBoxes from './OtpBoxes'
import {
  MFA_PENDING_KEY,
  mfaEnrollSetup,
  mfaEnrollVerify,
  mfaVerify,
} from '../_redux/authCrud'
// Local copy printed whatever the API sent, which let English internals — the
// error handler's httpStatus fallback, raw Joi output — onto the login screen.
import { apiMessage } from '../../../service/apiError'

function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
  }
  return Promise.reject(new Error('คัดลอกไม่สำเร็จ กรุณาคัดลอกด้วยตัวเอง'))
}

export default function MfaChallenge({ enrolled, onComplete, onCancel }) {
  const [enrolledLocal, setEnrolledLocal] = useState(enrolled)
  const [otpauthUri, setOtpauthUri] = useState('')
  const [manualKey, setManualKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [code, setCode] = useState('')
  const [copied, setCopied] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (enrolledLocal) return
    mfaEnrollSetup()
      .then(({ data }) => {
        const payload = data && data.data ? data.data : {}
        setOtpauthUri(payload.otpauthUri || '')
        setManualKey(payload.manualEntryKey || '')
      })
      .catch((err) => setError(apiMessage(err, 'เริ่มการลงทะเบียน MFA ไม่สำเร็จ')))
  }, [enrolledLocal])

  const finish = (payload) => {
    sessionStorage.removeItem(MFA_PENDING_KEY)
    onComplete(payload)
  }

  // The code has to arrive as an argument. OtpBoxes calls onChange(value) and
  // onComplete(value) in the same event, so `code` still holds the previous five
  // digits when onComplete runs — the first press always submitted a short code
  // and only the second press sent what the user had actually typed.
  const submitCode = (next) => {
    const value = typeof next === 'string' ? next : code
    if (loading || value.length !== 6) return
    setLoading(true)
    setError('')
    const req = enrolledLocal ? mfaVerify(value) : mfaEnrollVerify(value)
    req
      .then(({ data }) => finish(data && data.data ? data.data : {}))
      .catch((err) => setError(apiMessage(err, 'รหัส MFA ไม่ถูกต้อง')))
      .finally(() => setLoading(false))
  }

  const copyToClipboard = (label, text) =>
    copyText(text)
      .then(() => setCopied(label))
      .catch((err) => setError(err.message))

  return (
    <div className="login-form">
      <div className="text-center mb-8">
        <h3 className="font-size-h3 font-weight-bolder mb-2">
          {enrolledLocal ? 'ยืนยันรหัส 2 ขั้นตอน' : 'ตั้งค่า Google Authenticator'}
        </h3>
        <p className="text-dark-75 mb-0">
          {enrolledLocal
            ? 'กรอกรหัส 6 หลักจากแอป Authenticator ของคุณ'
            : 'สแกน QR แล้วกรอกรหัส 6 หลักเพื่อผูกเครื่องนี้'}
        </p>
      </div>
      {error ? (
        <div className="mb-6 alert alert-custom alert-light-danger">
          <div className="alert-text font-weight-bold">{error}</div>
        </div>
      ) : null}
      {!enrolledLocal && otpauthUri ? (
        <div className="text-center mb-6">
          <div className="d-inline-block bg-white p-4">
            <QRCode value={otpauthUri} size={200} level="M" />
          </div>
          <p className="text-dark-75 mt-4">
            เปิดแอป Google Authenticator หรือ Microsoft Authenticator แล้วกด + แล้วเลือกสแกน QR code
            <br />
            การสแกนด้วยกล้องของเครื่องหรือบนคอมพิวเตอร์จะเปิดแอปให้ไม่ได้ ต้องสแกนจากในแอปเท่านั้น
          </p>
          <p className="text-dark-75 mb-1">
            สแกนไม่ได้? เลือก "Enter a setup key" ในแอป แล้วกรอกคีย์นี้
          </p>
          <p className="font-weight-bold mb-2">
            {showKey ? manualKey : '•'.repeat(manualKey.length)}
          </p>
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-light-primary btn-sm font-weight-bold mr-3"
              onClick={() => setShowKey(!showKey)}
            >
              {showKey ? 'ซ่อนคีย์' : 'แสดงคีย์'}
            </button>
            <button
              type="button"
              className="btn btn-light-primary btn-sm font-weight-bold"
              onClick={() => copyToClipboard('key', manualKey)}
            >
              {copied === 'key' ? 'คัดลอกแล้ว' : 'คัดลอกคีย์'}
            </button>
          </div>
        </div>
      ) : null}

      <div>
        <OtpBoxes
          value={code}
          autoFocus
          invalid={Boolean(error)}
          disabled={loading}
          onChange={(next) => {
            setCode(next)
            setError('')
          }}
          onComplete={submitCode}
        />
        <button
          type="button"
          className="btn btn-primary btn-block font-weight-bold py-4 mt-6"
          disabled={loading || code.length !== 6}
          style={{ cursor: loading || code.length !== 6 ? 'not-allowed' : 'pointer' }}
          onClick={() => submitCode()}
        >
          ยืนยัน
          {loading && <span className="ml-3 spinner spinner-white"></span>}
        </button>
        {enrolledLocal ? (
          <p className="text-dark-75 text-center mt-4 mb-0">
            ใช้แอปยืนยันตัวตนไม่ได้หรือทำเครื่องหาย? ติดต่อผู้ดูแลระบบเพื่อรีเซ็ตการยืนยัน 2 ขั้นตอน
          </p>
        ) : null}
      </div>

      {onCancel ? (
        <div className="text-center mt-4">
          <button type="button" className="btn btn-link" onClick={onCancel}>
            กลับไปหน้าเข้าสู่ระบบ
          </button>
        </div>
      ) : null}
    </div>
  )
}
